import {
  ActionButton,
  ActionsGroup,
  Alert,
  Button,
  Chip,
  ExtraInfo,
  Loading,
  Panel,
  Table,
  useAuth
} from '@elotech/components';
import {
  SituacaoFuncionario,
  SituacaoFuncionarioLabels
} from 'common/type/SituacaoFuncionario';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { Roles } from '../../../roles/RolesPainel';
import { UsuarioFuncionarioService } from '../../../service';
import { UsuarioFuncionario } from '../../../type/UsuarioFuncionario';
import UsuarioFuncionarioForm from './UsuarioFuncionarioForm';

type Props = {
  cpf: string;
  title?: string;
  readonly?: boolean;
};

const UsuarioFuncionarioList: React.FC<Props> = ({
  cpf,
  title,
  readonly = false
}) => {
  const [funcionarios, setFuncionarios] = useState<UsuarioFuncionario[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingFuncionario, setEditingFuncionario] = useState<
    UsuarioFuncionario | undefined
  >(undefined);
  const [showButton, setShowButton] = useState(true);
  const { id } = useParams<{ id: string }>();
  const { hasRole } = useAuth();

  const fetchFuncionarios = useCallback(() => {
    setLoading(true);
    UsuarioFuncionarioService.findAll(cpf)
      .then(response => setFuncionarios(response.data))
      .catch(error => {
        setLoading(false);
        Alert.error({ title: `Erro ao buscar os funcionários` }, error);
      })
      .finally(() => setLoading(false));
  }, [cpf]);

  useEffect(() => {
    fetchFuncionarios();
  }, [fetchFuncionarios]);

  const onRemove = (index: number) => {
    Alert.question({
      title: 'Confirmação',
      text: 'Deseja realmente remover este funcionário?'
    }).then((result: any) => {
      if (result.value) {
        setLoading(true);
        UsuarioFuncionarioService.remove(funcionarios[index].id)
          .then(() => {
            fetchFuncionarios();
            Alert.success({
              text: 'Dados de funcionário removido com sucesso!'
            });
          })
          .catch(error => {
            setLoading(false);
            Alert.error(
              { title: 'Erro ao remover dados de funcionário' },
              error
            );
          })
          .finally(() => setLoading(false));
      }
    });
  };

  const handleEdit = (funcionarioId: string) => {
    setLoading(true);
    UsuarioFuncionarioService.findById(funcionarioId)
      .then(response => {
        setEditingFuncionario(response.data);
        setShowForm(true);
        setShowButton(false);
      })
      .catch(error => {
        setLoading(false);
        Alert.error({ title: 'Erro ao buscar dados de funcionário' }, error);
      })
      .finally(() => setLoading(false));
  };

  const handleFormState = (show: boolean = true) => {
    setShowForm(show);
    setShowButton(!show);

    if (!show) {
      setEditingFuncionario(undefined);
    }
  };

  const getSituacaoColor = (situacao: string) => {
    if (situacao === SituacaoFuncionario.ATIVO) return 'positive';
    if (situacao === SituacaoFuncionario.DESLIGADO) return 'negative';
    if (situacao === SituacaoFuncionario.FERIAS) return 'warning';
    return 'neutral';
  };

  const onSubmitFuncionario = (value: UsuarioFuncionario) => {
    value.usuarioId = id;
    setLoading(true);
    UsuarioFuncionarioService.save(value)
      .then(() => {
        Alert.success({
          text: `Dados de funcionário ${
            editingFuncionario ? 'atualizado' : 'adicionado'
          } com sucesso!`
        });
        fetchFuncionarios();
        handleFormState(false);
      })
      .catch(error => {
        setLoading(false);
        Alert.error(
          {
            title: `Erro ao ${
              editingFuncionario ? 'atualizar' : 'adicionar'
            } dados de funcionário`
          },
          error
        );
      });
  };

  return (
    <>
      {showForm && (
        <UsuarioFuncionarioForm
          initialValue={
            editingFuncionario ? { ...editingFuncionario } : undefined
          }
          onSubmit={onSubmitFuncionario}
          onCancel={() => handleFormState(false)}
        />
      )}
      <Panel
        isTable
        footer={
          !showForm && !readonly && hasRole(Roles.usuario_write.name) ? (
            <Button
              onClick={() => handleFormState()}
              iconPosition="left"
              className="mt-sm mr-sm mb-sm"
              position="right"
            >
              <i className="fa fa-plus" />
              Novo
            </Button>
          ) : null
        }
        title={title}
      >
        {loading ? (
          <Loading loading={loading} />
        ) : (
          <Table values={funcionarios}>
            <Table.Column<UsuarioFuncionario>
              header="Entidade"
              value={value => value.entidade?.nome || ''}
            />
            <Table.Column<UsuarioFuncionario>
              header="Situação"
              align="center"
              value={value => (
                <ExtraInfo
                  value={SituacaoFuncionarioLabels[value.situacao]}
                  color={getSituacaoColor(value.situacao)}
                />
              )}
            />
            <Table.Column<UsuarioFuncionario>
              header="Matrícula"
              value={value => value.matricula}
            />
            <Table.Column<UsuarioFuncionario>
              header="Cargo"
              value={value => value.cargo}
            />
            <Table.Column<UsuarioFuncionario>
              header="Lotação"
              value={value => value.lotacao}
            />
            <Table.Column<UsuarioFuncionario>
              header="Módulo cadastrado"
              value={value =>
                value.moduloCadastrado === 'Painel' ? (
                  <Chip value="Painel" color="positive" />
                ) : (
                  <Chip value="Folha Pagamento" color="neutral" />
                )
              }
            />
            <Table.Column<UsuarioFuncionario>
              header=""
              value={(_value, index) => (
                <ActionsGroup>
                  {!readonly &&
                    showButton &&
                    funcionarios[index].id &&
                    hasRole(Roles.usuario_write.name) && (
                      <>
                        <ActionButton
                          key="edit-button"
                          icon="pencil-alt"
                          label="Editar"
                          onClick={() => handleEdit(funcionarios[index].id)}
                        />
                        <ActionButton
                          key="remove-button"
                          icon="trash-alt"
                          label="Remover"
                          onClick={() => onRemove(index)}
                        />
                      </>
                    )}
                </ActionsGroup>
              )}
            />
          </Table>
        )}
      </Panel>
    </>
  );
};

export default UsuarioFuncionarioList;
